import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type FC,
} from "react";

export type Severity = "warning" | "success" | "error" | "info";

export const AlertContext = createContext<{
  alertText: string;
  setAlertText: React.Dispatch<React.SetStateAction<string>>;
  alertActionLabel?: string;
  setAlertActionLabel: React.Dispatch<React.SetStateAction<string | undefined>>;
  alertAction?: (() => void) | undefined;
  setAlertAction: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
  position: string;
  setPosition: React.Dispatch<React.SetStateAction<string>>;
  severity: Severity;
  setSeverity: React.Dispatch<React.SetStateAction<Severity>>;
  setTime: React.Dispatch<React.SetStateAction<number | undefined>>;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setHideGeneralAlert: React.Dispatch<React.SetStateAction<boolean>>;
  hideGeneralAlert: boolean;
  isSidebarOpen: boolean;
} | null>(null);

export const useAlert = () => useContext(AlertContext)!;

interface Props {
  children?: React.ReactNode;
}

export const AlertProvider: FC<Props> = ({ children }) => {
  const [severity, setSeverity] = useState<Severity>("warning");
  const [alertText, setAlertText] = useState<string>("");
  const [alertActionLabel, setAlertActionLabel] = useState<string>();
  const [alertAction, setAlertAction] = useState<(() => void) | undefined>();
  const [position, setPosition] = useState<string>("");
  const [time, setTime] = useState<number>();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [hideGeneralAlert, setHideGeneralAlert] = useState<boolean>(false);

  useEffect(() => {
    if (!alertText) return;

    // Keep actionable alerts visible until the user acts or dismisses manually.
    if (alertAction) return;

    const timeoutId = setTimeout(
      () => {
        setPosition("");
        setSeverity("warning");
        setAlertText("");
        setTime(undefined);
        setHideGeneralAlert(false);
        setAlertActionLabel(undefined);
        setAlertAction(undefined);
      },
      time ? time : severity === "warning" ? 3000 : 2000,
    );

    return () => clearTimeout(timeoutId);
  }, [alertText, alertAction, time, severity]);

  return (
    <AlertContext.Provider
      value={{
        alertText,
        alertActionLabel,
        setAlertActionLabel,
        alertAction,
        setAlertAction,
        position,
        setAlertText,
        setPosition,
        setSeverity,
        severity,
        setTime,
        isSidebarOpen,
        setIsSidebarOpen,
        hideGeneralAlert,
        setHideGeneralAlert
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};
